import React, { useMemo, useState } from 'react';
import { Loader2, Pencil, Plus, RefreshCw, Trash2, X, ChevronDown } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import vendorApi from '../../../api/vendorApi';
import ledgerViewsApi from '../../../api/ledgerViewsApi';
import {
    CreateStandardAccountRequest,
    QboAccount,
    StandardAccount,
    UpdateQboAccountMappingRequest,
    UpdateStandardAccountRequest,
} from '../../../api/types';
import { useWorkspace } from '../../../context/WorkspaceContext';
import StandardAccountSelector from '../../ledger/components/StandardAccountSelector';

type GLCodeView = 'finz' | 'qbo';

type AccountFormState = {
    fiscal_bucket: string;
    account_name: string;
    account_number: string;
    notes: string;
    non_operating: boolean;
};

const EMPTY_FORM: AccountFormState = {
    fiscal_bucket: '',
    account_name: '',
    account_number: '',
    notes: '',
    non_operating: false,
};

const getDisplayName = (account: StandardAccount): string => account.display_name || account.account_name || 'Untitled Account';
const getDisplayBucket = (bucket?: string | null): string => ((bucket || '').trim().toUpperCase() === 'INTERNAL TRANSFER' ? 'Non-Operating Items' : (bucket || ''));

const compareAccountsByNumber = (left: StandardAccount, right: StandardAccount): number => {
    const leftNumber = (left.account_number || '').trim();
    const rightNumber = (right.account_number || '').trim();

    if (leftNumber && rightNumber) {
        const leftIsNumeric = /^\d+$/.test(leftNumber);
        const rightIsNumeric = /^\d+$/.test(rightNumber);
        if (leftIsNumeric && rightIsNumeric) {
            const delta = Number(leftNumber) - Number(rightNumber);
            if (delta !== 0) return delta;
        } else {
            const byString = leftNumber.localeCompare(rightNumber, undefined, { numeric: true });
            if (byString !== 0) return byString;
        }
    } else if (leftNumber || rightNumber) {
        return leftNumber ? -1 : 1;
    }

    return getDisplayName(left).localeCompare(getDisplayName(right));
};

const getQboAccountKey = (account: QboAccount): string => account.data.account_id || account._id;

const getMappingLabel = (account: QboAccount): string => {
    const mapping = account.mapping;
    if (!mapping?.standard_account_id) return 'Unmapped';
    const number = mapping.finz_account_number?.trim();
    const name = mapping.finz_account_name || 'Unnamed Finz Account';
    const bucketLabel = getDisplayBucket(mapping.fiscal_bucket);
    const bucket = bucketLabel ? ` · ${bucketLabel}` : '';
    return `${number ? `${number} · ` : ''}${name}${bucket}`;
};

const getMappingStatusTone = (status?: string): string => {
    switch (status) {
        case 'mapped_manual':
            return 'bg-emerald-50 text-emerald-700';
        case 'mapped_exact':
            return 'bg-blue-50 text-blue-700';
        case 'mapped_llm':
            return 'bg-violet-50 text-violet-700';
        case 'mapped_auto':
            return 'bg-sky-50 text-sky-700';
        case 'excluded':
            return 'bg-gray-100 text-gray-600';
        default:
            return 'bg-amber-50 text-amber-700';
    }
};

export default function GLCode() {
    return <GLCodeContent />;
}

type GLCodeProps = {
    businessIdOverride?: string;
    heading?: string;
    subheading?: string;
    defaultViewMode?: GLCodeView;
    lockedViewMode?: GLCodeView | null;
    showHeading?: boolean;
};

export function GLCodeContent({
    businessIdOverride,
    heading = 'GL Code',
    subheading = 'Manage Finz accounts for the active business.',
    defaultViewMode = 'finz',
    lockedViewMode = null,
    showHeading = true,
}: GLCodeProps) {
    const queryClient = useQueryClient();
    const { activeBusinessId } = useWorkspace();
    const businessId = businessIdOverride || activeBusinessId || undefined;

    const [viewMode, setViewMode] = useState<GLCodeView>(lockedViewMode || defaultViewMode);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingAccount, setEditingAccount] = useState<StandardAccount | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
    const [formState, setFormState] = useState<AccountFormState>(EMPTY_FORM);

    const { data, isLoading } = useQuery({
        queryKey: ['standardAccounts', businessId],
        queryFn: () => vendorApi.getStandardAccounts(businessId),
        enabled: !!businessId,
    });

    const { data: qboAccountsData, isLoading: isQboAccountsLoading } = useQuery({
        queryKey: ['qboAccounts', businessId],
        queryFn: () => vendorApi.getQBOAccounts(businessId),
        enabled: !!businessId,
    });

    const [selectedQboAccountId, setSelectedQboAccountId] = useState('');
    const [selectedFinzAccountId, setSelectedFinzAccountId] = useState('');
    const [selectedQboMappingStatus, setSelectedQboMappingStatus] = useState('');
    const [selectedQboType, setSelectedQboType] = useState('');
    const [selectorAccount, setSelectorAccount] = useState<QboAccount | null>(null);
    const effectiveViewMode = lockedViewMode || viewMode;

    const accounts = useMemo(() => data?.items || [], [data?.items]);

    const categories = useMemo(() => {
        return Array.from(new Set([...accounts.map((account) => getDisplayBucket(account.fiscal_bucket)).filter(Boolean), 'Non-Operating Items'])).sort((a, b) => a.localeCompare(b));
    }, [accounts]);

    const filteredCategories = useMemo(() => {
        const query = formState.fiscal_bucket.trim().toLowerCase();
        if (!query) return categories;
        return categories.filter((category) => category.toLowerCase().includes(query));
    }, [categories, formState.fiscal_bucket]);

    const filteredAccounts = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const scoped = !query ? accounts : accounts.filter((account) => {
            const displayName = getDisplayName(account).toLowerCase();
            const accountNumber = (account.account_number || '').toLowerCase();
            const bucket = getDisplayBucket(account.fiscal_bucket).toLowerCase();
            const notes = (account.notes || '').toLowerCase();
            return (
                displayName.includes(query) ||
                accountNumber.includes(query) ||
                bucket.includes(query) ||
                notes.includes(query)
            );
        });
        return [...scoped].sort(compareAccountsByNumber);
    }, [accounts, searchTerm]);

    const sortedAccountOptions = useMemo(() => [...accounts].sort(compareAccountsByNumber), [accounts]);

    const resetForm = () => {
        setEditingAccount(null);
        setFormState(EMPTY_FORM);
        setIsFormOpen(false);
        setIsCategoryMenuOpen(false);
    };

    const invalidateAccounts = () => {
        queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] });
    };

    const invalidateQboAccounts = () => {
        queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] });
    };

    const createMutation = useMutation({
        mutationFn: (payload: CreateStandardAccountRequest) => vendorApi.createStandardAccount(payload, businessId),
        onSuccess: () => {
            invalidateAccounts();
            invalidateQboAccounts();
            resetForm();
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ accountId, payload }: { accountId: string; payload: UpdateStandardAccountRequest }) =>
            vendorApi.updateStandardAccount(accountId, payload, businessId),
        onSuccess: () => {
            invalidateAccounts();
            invalidateQboAccounts();
            resetForm();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (accountId: string) => vendorApi.deleteStandardAccount(accountId, businessId),
        onSuccess: () => {
            invalidateAccounts();
            invalidateQboAccounts();
        },
    });

    const syncQboMutation = useMutation({
        mutationFn: () => ledgerViewsApi.refreshAccounting(businessId),
        onSuccess: async () => {
            await invalidateQboAccounts();
        },
    });

    const syncQboMappingsMutation = useMutation({
        mutationFn: () => vendorApi.syncQboAccountMappings(businessId),
        onSuccess: async () => {
            await invalidateQboAccounts();
        },
    });

    const updateQboMappingMutation = useMutation({
        mutationFn: ({ qboAccountId, payload }: { qboAccountId: string; payload: UpdateQboAccountMappingRequest }) =>
            vendorApi.updateQboAccountMapping(qboAccountId, payload, businessId),
        onSuccess: async () => {
            await invalidateQboAccounts();
        },
    });

    const openCreateForm = () => {
        setEditingAccount(null);
        setFormState({
            ...EMPTY_FORM,
            fiscal_bucket: categories[0] || '',
        });
        setIsFormOpen(true);
    };

    const openEditForm = (account: StandardAccount) => {
        setEditingAccount(account);
        setFormState({
            fiscal_bucket: getDisplayBucket(account.fiscal_bucket) || '',
            account_name: getDisplayName(account),
            account_number: account.account_number || '',
            notes: account.notes || '',
            non_operating: !!account.non_operating || getDisplayBucket(account.fiscal_bucket) === 'Non-Operating Items',
        });
        setIsFormOpen(true);
    };

    const handleSave = () => {
        if (!formState.fiscal_bucket.trim() || !formState.account_name.trim()) return;

        const payload = {
            fiscal_bucket: formState.fiscal_bucket.trim(),
            account_name: formState.account_name.trim(),
            account_number: formState.account_number.trim() || undefined,
            notes: formState.notes.trim() || undefined,
            fs_category: 'Income Statement',
            non_operating: formState.non_operating,
        };

        if (editingAccount) {
            updateMutation.mutate({ accountId: editingAccount._id, payload });
            return;
        }

        createMutation.mutate(payload);
    };

    const handleDelete = (account: StandardAccount) => {
        if (!window.confirm(`Delete "${getDisplayName(account)}"?`)) return;
        deleteMutation.mutate(account._id);
    };

    const qboAccountOptions = useMemo(() => {
        const groups = qboAccountsData?.groups || {};
        return Object.entries(groups)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([groupName, items]) => ({
                groupName,
                items: [...items].sort((left, right) => {
                    const leftName = left.data.fully_qualified_name || left.data.name || '';
                    const rightName = right.data.fully_qualified_name || right.data.name || '';
                    return leftName.localeCompare(rightName);
                }),
            }));
    }, [qboAccountsData]);

    const flattenedQboAccounts = useMemo(() => {
        return qboAccountOptions.flatMap((group) =>
            group.items.map((account) => ({
                groupName: group.groupName,
                account,
            }))
        );
    }, [qboAccountOptions]);

    const qboTypeOptions = useMemo(() => {
        return Array.from(
            new Set(flattenedQboAccounts.map(({ account }) => account.data.account_type).filter(Boolean))
        ).sort((left, right) => String(left).localeCompare(String(right)));
    }, [flattenedQboAccounts]);

    const filteredQboAccounts = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        let scoped = selectedQboAccountId
            ? flattenedQboAccounts.filter(({ account }) => getQboAccountKey(account) === selectedQboAccountId)
            : flattenedQboAccounts;

        if (selectedFinzAccountId) {
            scoped = scoped.filter(({ account }) => account.mapping?.standard_account_id === selectedFinzAccountId);
        }

        if (selectedQboMappingStatus) {
            scoped = scoped.filter(({ account }) => (account.mapping?.status || 'needs_review') === selectedQboMappingStatus);
        }

        if (selectedQboType) {
            scoped = scoped.filter(({ account }) => (account.data.account_type || '') === selectedQboType);
        }

        if (!query) return scoped;

        return scoped.filter(({ account, groupName }) => {
            const qualifiedName = (account.data.fully_qualified_name || '').toLowerCase();
            const plainName = (account.data.name || '').toLowerCase();
            const accountId = (account.data.account_id || '').toLowerCase();
            const accountType = (account.data.account_type || '').toLowerCase();
            return (
                qualifiedName.includes(query) ||
                plainName.includes(query) ||
                accountId.includes(query) ||
                accountType.includes(query) ||
                groupName.toLowerCase().includes(query)
            );
        });
    }, [flattenedQboAccounts, searchTerm, selectedQboAccountId, selectedFinzAccountId, selectedQboMappingStatus, selectedQboType]);

    const activeSearchPlaceholder = effectiveViewMode === 'finz'
        ? 'Search account, category, number, or notes...'
        : 'Search QBO account name, id, type, or group...';

    return (
        <div className="flex h-full min-h-0 flex-col">
            {showHeading && (
                <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">{heading}</h2>
                        <p className="mt-1 text-sm text-gray-500">{subheading}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        {!lockedViewMode && (
                            <select
                                value={viewMode}
                                onChange={(event) => {
                                    setViewMode(event.target.value as GLCodeView);
                                    setSearchTerm('');
                                    setSelectedQboAccountId('');
                                    setSelectedFinzAccountId('');
                                    setSelectedQboMappingStatus('');
                                    setSelectedQboType('');
                                }}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 pr-8 text-sm shadow-sm focus:border-gray-300 focus:outline-none focus:ring-0"
                            >
                                <option value="finz">Finz Accounts</option>
                                <option value="qbo">QBO Accounts</option>
                            </select>
                        )}
                        {effectiveViewMode === 'qbo' && (
                            <button
                                type="button"
                                onClick={() => syncQboMappingsMutation.mutate()}
                                disabled={!businessId || syncQboMappingsMutation.isPending}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                            >
                                {syncQboMappingsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                Refresh Mappings
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => syncQboMutation.mutate()}
                            disabled={!businessId || syncQboMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
                        >
                            {syncQboMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Sync QBO
                        </button>
                    </div>
                </div>
            )}

            <div className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-4">
                <div className="flex w-full flex-wrap items-center gap-4">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder={activeSearchPlaceholder}
                        className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />

                    {effectiveViewMode === 'qbo' ? (
                        <>
                            <select
                                value={selectedQboAccountId}
                                onChange={(event) => setSelectedQboAccountId(event.target.value)}
                                disabled={!businessId || isQboAccountsLoading}
                                className="block w-[280px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                            >
                                <option value="">
                                    {isQboAccountsLoading ? 'Loading QBO accounts...' : `All QBO accounts (${qboAccountsData?.total_count || 0})`}
                                </option>
                                {qboAccountOptions.map((group) => (
                                    <optgroup key={group.groupName} label={group.groupName}>
                                        {group.items.map((account) => (
                                            <option key={account._id} value={getQboAccountKey(account)}>
                                                {account.data.fully_qualified_name || account.data.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>

                            <select
                                value={selectedFinzAccountId}
                                onChange={(event) => setSelectedFinzAccountId(event.target.value)}
                                disabled={!businessId || isLoading}
                                className="block w-[280px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
                            >
                                <option value="">{isLoading ? 'Loading Finz accounts...' : `All Finz accounts (${sortedAccountOptions.length})`}</option>
                                {sortedAccountOptions.map((standardAccount) => (
                                    <option key={standardAccount._id} value={standardAccount._id}>
                                        {standardAccount.account_number ? `${standardAccount.account_number} · ` : ''}{getDisplayName(standardAccount)}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={selectedQboMappingStatus}
                                onChange={(event) => setSelectedQboMappingStatus(event.target.value)}
                                className="block w-[180px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                                <option value="">All statuses</option>
                                <option value="mapped_manual">Manual</option>
                                <option value="mapped_exact">Exact</option>
                                <option value="mapped_llm">LLM</option>
                                <option value="mapped_auto">Legacy Auto</option>
                                <option value="needs_review">Needs review</option>
                                <option value="excluded">Excluded</option>
                            </select>

                            <select
                                value={selectedQboType}
                                onChange={(event) => setSelectedQboType(event.target.value)}
                                className="block w-[180px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                                <option value="">All QBO types</option>
                                {qboTypeOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </>
                    ) : (
                        <div className="h-10" />
                    )}
                </div>

                <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-500">
                        {effectiveViewMode === 'finz' ? `${filteredAccounts.length} accounts` : `${filteredQboAccounts.length} accounts`}
                    </div>
                    {effectiveViewMode === 'finz' && (
                        <button
                            type="button"
                            onClick={openCreateForm}
                            disabled={!businessId}
                            className="inline-flex items-center gap-2 rounded-lg bg-[#5B5DE4] px-4 py-2 text-sm font-medium text-white hover:bg-[#4b4dcf] disabled:cursor-not-allowed disabled:bg-indigo-300"
                        >
                            <Plus className="h-4 w-4" />
                            Add Account
                        </button>
                    )}
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white">
                {effectiveViewMode === 'finz' ? (
                    isLoading ? (
                        <div className="flex h-full min-h-[240px] items-center justify-center text-gray-400">
                            <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                    ) : (
                        <table className="w-full border-collapse text-left">
                            <thead className="sticky top-0 bg-gray-50">
                                <tr className="text-sm font-medium text-gray-500">
                                    <th className="px-4 py-3">Category</th>
                                    <th className="px-4 py-3">Account</th>
                                    <th className="px-4 py-3">Number</th>
                                    <th className="px-4 py-3">Notes</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccounts.map((account) => (
                                    <tr key={account._id} className="border-t border-gray-100 text-sm text-gray-700">
                                            <td className="px-4 py-3">{getDisplayBucket(account.fiscal_bucket)}</td>
                                            <td className="px-4 py-3 font-medium text-gray-900">{getDisplayName(account)}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{account.account_number || '—'}</td>
                                        <td className="max-w-[260px] truncate px-4 py-3 text-gray-500">{account.notes || '—'}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => openEditForm(account)}
                                                    className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(account)}
                                                    className="rounded-md p-2 text-red-500 hover:bg-red-50 hover:text-red-600"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredAccounts.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-400">
                                            No accounts found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )
                ) : isQboAccountsLoading ? (
                    <div className="flex h-full min-h-[240px] items-center justify-center text-gray-400">
                        <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                ) : (
                    <table className="w-full border-collapse text-left">
                        <thead className="sticky top-0 bg-gray-50">
                            <tr className="text-sm font-medium text-gray-500">
                                <th className="px-4 py-3">Group</th>
                                <th className="px-4 py-3">QBO Account</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Classification</th>
                                <th className="px-4 py-3">Finz Mapping</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQboAccounts.map(({ groupName, account }) => (
                                <tr key={account._id} className="border-t border-gray-100 text-sm text-gray-700">
                                    <td className="px-4 py-3">{groupName}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">
                                            {account.data.fully_qualified_name || account.data.name}
                                        </div>
                                        <div className="mt-1 font-mono text-xs text-gray-500">
                                            {account.data.account_id || '—'}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">{account.data.account_type || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500">{account.data.classification || '—'}</td>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            onClick={() => setSelectorAccount(account)}
                                            disabled={!businessId || updateQboMappingMutation.isPending}
                                            className="mt-2 inline-flex w-full min-w-[280px] items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
                                        >
                                            <span>{getMappingLabel(account)}</span>
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getMappingStatusTone(account.mapping?.status)}`}>
                                            {account.mapping?.status || 'needs_review'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-700">
                                        {typeof account.data.current_balance === 'number'
                                            ? account.data.current_balance.toLocaleString('en-US', {
                                                style: 'currency',
                                                currency: account.data.currency || 'USD',
                                            })
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                            {filteredQboAccounts.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-400">
                                        No QBO accounts found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {isFormOpen && (
                <div className="fixed inset-0 z-[3010] flex items-center justify-center bg-black/20 p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                            <h4 className="text-base font-semibold text-gray-900">{editingAccount ? 'Edit Account' : 'Add Account'}</h4>
                            <button type="button" onClick={resetForm} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500">
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="space-y-4 px-5 py-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                                <div className="relative">
                                    <input
                                        value={formState.fiscal_bucket}
                                        onFocus={() => setIsCategoryMenuOpen(true)}
                                        onChange={(event) => {
                                            const nextBucket = event.target.value;
                                            setFormState((prev) => ({
                                                ...prev,
                                                fiscal_bucket: nextBucket,
                                                non_operating: nextBucket.trim().toLowerCase() === 'non-operating items' ? true : prev.non_operating,
                                            }));
                                            setIsCategoryMenuOpen(true);
                                        }}
                                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="Select or type a category"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsCategoryMenuOpen((prev) => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500"
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                    {isCategoryMenuOpen && filteredCategories.length > 0 && (
                                        <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                                            {filteredCategories.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => {
                                                        setFormState((prev) => ({
                                                            ...prev,
                                                            fiscal_bucket: category,
                                                            non_operating: category.trim().toLowerCase() === 'non-operating items' ? true : prev.non_operating,
                                                        }));
                                                        setIsCategoryMenuOpen(false);
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                >
                                                    {category}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Account Name</label>
                                <input
                                    value={formState.account_name}
                                    onChange={(event) => setFormState((prev) => ({ ...prev, account_name: event.target.value }))}
                                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Insurance - Property"
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Account Number <span className="font-normal text-gray-400">(Optional)</span></label>
                                <input
                                    value={formState.account_number}
                                    onChange={(event) => setFormState((prev) => ({ ...prev, account_number: event.target.value }))}
                                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="7210"
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                    rows={3}
                                    value={formState.notes}
                                    onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
                                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Optional notes"
                                />
                            </div>

                            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={formState.non_operating}
                                    onChange={(event) => setFormState((prev) => ({ ...prev, non_operating: event.target.checked }))}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Non-operating</div>
                                    <div className="text-xs text-gray-500">Transactions mapped to this account default to `operating_flag = false`.</div>
                                </div>
                            </label>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
                            <button
                                type="button"
                                onClick={resetForm}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={
                                    createMutation.isPending ||
                                    updateMutation.isPending ||
                                    !formState.fiscal_bucket.trim() ||
                                    !formState.account_name.trim()
                                }
                                className="inline-flex items-center gap-2 rounded-lg bg-[#5B5DE4] px-4 py-2 text-sm font-medium text-white hover:bg-[#4b4dcf] disabled:cursor-not-allowed disabled:bg-indigo-300"
                            >
                                {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                                {editingAccount ? 'Save Changes' : 'Create Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectorAccount && (
                <StandardAccountSelector
                    isOpen={true}
                    onClose={() => setSelectorAccount(null)}
                    currentAccountId={selectorAccount.mapping?.standard_account_id || null}
                    businessId={businessId}
                    onSelect={(standardAccount) => {
                        updateQboMappingMutation.mutate({
                            qboAccountId: getQboAccountKey(selectorAccount),
                            payload: {
                                standard_account_id: standardAccount._id === 'internal-transfer' ? null : standardAccount._id,
                                apply_to_transactions: true,
                            },
                        });
                        setSelectorAccount(null);
                    }}
                />
            )}
        </div>
    );
}
